// src/black_hole_game.ts
var OutOfBoundsError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "OutOfBoundsError";
  }
};
var FilledCellError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "FilledCellError";
  }
};
var GameOverError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "GameOverError";
  }
};
var GameInProgressError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "GameInProgressError";
  }
};
var DEFAULT_BOARD_ROWS = 6;
var Board = class _Board {
  _cells;
  _filled;
  _sparse_scoring;
  constructor(rows = DEFAULT_BOARD_ROWS, cells, filled = 0, sparseScoring = false) {
    if (cells) {
      this._cells = cells;
    } else {
      const initialCells = [];
      for (let i = 1; i <= rows; i++) {
        initialCells.push(new Array(i).fill(0));
      }
      this._cells = initialCells;
    }
    this._filled = filled;
    this._sparse_scoring = sparseScoring;
  }
  /** Number of rows on the (triangular) board. */
  get rows() {
    return this._cells.length;
  }
  /** Total number of cells on the board. */
  get cellCount() {
    return this._cells.length * (this._cells.length + 1) / 2;
  }
  /** Number of cells filled so far. */
  get filled() {
    return this._filled;
  }
  /** Whether this board uses sparse ("black hole") scoring. */
  get sparseScoring() {
    return this._sparse_scoring;
  }
  nextMove() {
    if (this._filled % 2 === 0) {
      return Math.floor(this._filled / 2) + 1;
    }
    return -(Math.floor(this._filled / 2) + 1);
  }
  _isValid(row, col) {
    return 0 <= row && row < this._cells.length && (0 <= col && col <= row);
  }
  play(row, col) {
    if (!this._isValid(row, col)) {
      throw new OutOfBoundsError();
    }
    if (this._cells[row][col] !== 0) {
      throw new FilledCellError();
    }
    if (this.isGameOver()) {
      throw new GameOverError();
    }
    const cells = [];
    for (let i = 0; i < this._cells.length; i++) {
      if (i === row) {
        const newRow = [...this._cells[i]];
        newRow[col] = this.nextMove();
        cells.push(newRow);
      } else {
        cells.push([...this._cells[i]]);
      }
    }
    return new _Board(this.rows, cells, this._filled + 1, this._sparse_scoring);
  }
  at(row, col) {
    if (!this._isValid(row, col)) {
      throw new OutOfBoundsError();
    }
    return this._cells[row][col];
  }
  /**
   * Returns the board with the most recently placed piece removed (a no-op on
   * an empty board). Move order is recoverable from the piece magnitudes —
   * players alternate +1, -1, +2, -2, … — so the piece placed last is the one
   * whose ply index is `filled - 1`. This lets undo reconstruct earlier
   * positions from the board alone, with no separate move-history stack to
   * drift out of sync.
   */
  undoMove() {
    if (this._filled === 0) {
      return this;
    }
    const lastPly = this._filled - 1;
    const magnitude = Math.floor(lastPly / 2) + 1;
    const lastValue = lastPly % 2 === 0 ? magnitude : -magnitude;
    const cells = this._cells.map((row) => [...row]);
    for (let row = 0; row < cells.length; row++) {
      for (let col = 0; col <= row; col++) {
        if (cells[row][col] === lastValue) {
          cells[row][col] = 0;
          return new _Board(this.rows, cells, this._filled - 1, this._sparse_scoring);
        }
      }
    }
    return this;
  }
  isGameOver() {
    return this._filled >= this.cellCount - 1;
  }
  _lockedInScore() {
    let player1Score = 0;
    let player2Score = 0;
    for (let i = 0; i < this._cells.length; i++) {
      for (let j = 0; j <= i; j++) {
        if (this._cells[i][j] === 0) {
          continue;
        }
        let isLockedIn = true;
        for (const cell of around(i, j, this.rows)) {
          if (this._cells[cell.row][cell.col] === 0) {
            isLockedIn = false;
            break;
          }
        }
        if (isLockedIn) {
          if (this._cells[i][j] > 0) {
            player1Score += this._cells[i][j];
          } else if (this._cells[i][j] < 0) {
            player2Score += Math.abs(this._cells[i][j]);
          }
        }
      }
    }
    return { player1: player1Score, player2: player2Score };
  }
  lockedInDiff() {
    const { player1, player2 } = this._lockedInScore();
    return player1 - player2;
  }
  /**
   * Whether the piece at (row, col) is "safe"/"locked-in": it is occupied and
   * has no empty neighbor, so its value can no longer change hands. Empty cells
   * are never safe. Useful for the safe-piece banking visualization.
   */
  isSafe(row, col) {
    if (this.at(row, col) === 0) {
      return false;
    }
    for (const cell of around(row, col, this.rows)) {
      if (this._cells[cell.row][cell.col] === 0) {
        return false;
      }
    }
    return true;
  }
  scores() {
    if (this._sparse_scoring && !this.isGameOver()) {
      throw new GameInProgressError();
    }
    if (!this._sparse_scoring) {
      return this._lockedInScore();
    }
    let row = -1;
    let col = -1;
    for (let i = 0; i < this._cells.length; i++) {
      let found = false;
      for (let j = 0; j <= i; j++) {
        if (this._cells[i][j] === 0) {
          row = i;
          col = j;
          found = true;
          break;
        }
      }
      if (found) {
        break;
      }
    }
    if (row === -1) {
      throw new Error("no unfilled cell");
    }
    let player1Score = 0;
    let player2Score = 0;
    for (const cell of around(row, col, this.rows)) {
      const val = this._cells[cell.row][cell.col];
      if (val > 0) {
        player2Score += val;
      } else if (val < 0) {
        player1Score += Math.abs(val);
      }
    }
    return { player1: player1Score, player2: player2Score };
  }
};
function* around(row, col, rows = DEFAULT_BOARD_ROWS) {
  if (row - 1 >= 0) {
    if (col - 1 >= 0) {
      yield { row: row - 1, col: col - 1 };
    }
    if (col <= row - 1) {
      yield { row: row - 1, col };
    }
  }
  if (col - 1 >= 0) {
    yield { row, col: col - 1 };
  }
  if (col + 1 <= row) {
    yield { row, col: col + 1 };
  }
  if (row + 1 < rows) {
    yield { row: row + 1, col };
    if (col + 1 <= row + 1) {
      yield { row: row + 1, col: col + 1 };
    }
  }
}

export {
  OutOfBoundsError,
  FilledCellError,
  GameOverError,
  GameInProgressError,
  DEFAULT_BOARD_ROWS,
  Board,
  around
};
//# sourceMappingURL=chunk-SGMKTIBR.js.map